import React, { type ReactNode, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { change, formValueSelector } from "redux-form";
import { MerchantNameInput } from "./inputs/MerchantNameInput";
import MerchantWebsiteInput from "./inputs/MerchantWebsiteInput";
import MerchantLogoUploadForm from "./MerchantLogoUploadForm";
import MerchantTermTemplatesLoader from "./MerchantTermTemplatesLoader";
import type { ThunkDispatchType } from "src/app/reducers/IMainAppState";
import { useAccessible } from "src/authentication/hooks/useAccessible";
import { GetMerchantGroups } from "src/merchantgroups/actions/MerchantGroupActionCreators";
import { MerchantGroupAccessRight } from "src/merchantgroups/enums/MerchantGroupAccessRight";
import MerchantGroupInput from "src/merchantgroups/forms/inputs/MerchantGroupInput";
import type { MerchantGroup } from "src/merchantgroups/objects/MerchantGroup";
import { MerchantAccessRight } from "src/merchants/enums/MerchantAccessRight";
import { MerchantLogo } from "src/merchants/formatters/MerchantFormatters";
import { MerchantCooldownIntervalInput } from "src/merchants/forms/MerchantCooldownIntervalInput";
import { MerchantFormNumberProvisioning } from "src/merchants/forms/MerchantFormNumberProvisioning";
import type { Merchant } from "src/merchants/objects/Merchant";
import Loader from "src/shared/components/Loader";
import ContactAddressForm from "src/shared/contacts/forms/ContactAddressForm";
import ContactContactForm from "src/shared/contacts/forms/ContactContactForm";
import { CONTACT_ADDRESS_COUNTRY_CODE } from "src/shared/contacts/forms/inputs/address/IAddressData";
import EmailInput from "src/shared/forms/inputs/EmailInput";
import { Step } from "src/shared/forms/steps/Step";
import { Steps } from "src/shared/forms/steps/Steps";
import EmptyTooltip from "src/shared/forms/tooltips/EmptyTooltip";
import TermForm from "src/shared/term/form/TermForm";

export const MERCHANT_MERCHANT_GROUP_UUID = "merchantGroupUUID";
export const MERCHANT_NAME = "merchantName";
export const MERCHANT_SHORT_NAME = "merchantShortName";
export const MERCHANT_WEBSITE = "webSite";
export const MERCHANT_COOLDOWN_INTERVAL = "cooldownInterval";
export const MERCHANT_LOGO = "logoUrl";
export const MERCHANT_SUPPORT_EMAIL = "supportEmail";
export const MERCHANT_TERM = "merchantTerm";
export const MERCHANT_PROVISIONING_TYPE = "provisioningType";
export const MERCHANT_PROVIDER_ID = "providerId";
export const MERCHANT_MOBILE_VIRTUAL_NUMBER = "mobileVirtualNumber";
export const MERCHANT_IS_CHAT = "isChat";
export const MERCHANT_IS_TWO_STEP_OPT_IN = "isTwoStepOptIn";
export const MERCHANT_CARRIER_GO_LIVE = "carrierGoLive";
export const MERCHANT_IS_DISPATCH_EXTENDED = "isDispatchExtended";
export const MERCHANT_MONTHLY_VOLUME_EXPECTED = "monthlyVolumeExpected";
export const MERCHANT_MONTHLY_VOLUME_LIMIT = "monthlyVolumeLimit";
export const MERCHANT_IS_MMS = "isMms";

export interface IMerchantFormProps {
  merchant?: Merchant;
  form: string;
  showMerchantGroupSelect?: boolean;
  children: ReactNode;
  nextButtonDisabled?: boolean;
  countriesDisabled?: boolean;
  isEdit?: boolean;
}

export default (props: IMerchantFormProps) => {
  const {
    children,
    merchant,
    form,
    showMerchantGroupSelect,
    nextButtonDisabled,
    countriesDisabled,
    isEdit,
    ...rest
  } = props;

  const selector = formValueSelector(props.form);
  const countryCode = useSelector((state) =>
    selector(state, CONTACT_ADDRESS_COUNTRY_CODE),
  );
  const merchantLogo = useSelector((state) => selector(state, MERCHANT_LOGO));

  const [merchantGroupsPromise, setMerchantGroupsPromise] = useState(undefined);

  const canViewEditCooldown = useAccessible(
    MerchantAccessRight.VIEW_EDIT_COOLDOWN_INTERVAL,
  );
  const isMerchantGroupsAccessible = useAccessible(
    MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS,
  );

  const dispatch: ThunkDispatchType = useDispatch();

  const handleMerchantLogoChange = useCallback(
    (logoUrl: string) => {
      dispatch(change(form, MERCHANT_LOGO, logoUrl));
    },
    [dispatch, form],
  );

  useEffect(() => {
    if (showMerchantGroupSelect && isMerchantGroupsAccessible) {
      setMerchantGroupsPromise(dispatch(GetMerchantGroups()));
    }
  }, [dispatch, showMerchantGroupSelect, isMerchantGroupsAccessible]);

  return (
    <Steps
      {...rest}
      lastStepRender={children}
      nextButtonDisabled={nextButtonDisabled || !merchantLogo}
    >
      <Step name={<FormattedMessage id="merchants.form.basic" />}>
        {() => (
          <>
            <MerchantNameInput
              inputID={MERCHANT_NAME}
              tooltip={<EmptyTooltip />}
            />

            <MerchantNameInput
              inputID={MERCHANT_SHORT_NAME}
              label={<FormattedMessage id="merchants.shortName" />}
              tooltip={<EmptyTooltip />}
              maxLength={30}
              disabled={isEdit}
              validationDisabled={isEdit}
            />

            <MerchantWebsiteInput inputID={MERCHANT_WEBSITE} />

            {showMerchantGroupSelect && isMerchantGroupsAccessible && (
              <Loader promise={merchantGroupsPromise}>
                {(merchantGroups: MerchantGroup[]) => (
                  <MerchantGroupInput
                    inputID={MERCHANT_MERCHANT_GROUP_UUID}
                    merchantGroups={merchantGroups}
                    tooltip={<EmptyTooltip />}
                  />
                )}
              </Loader>
            )}

            {canViewEditCooldown && merchant && (
              <MerchantCooldownIntervalInput
                inputID={MERCHANT_COOLDOWN_INTERVAL}
                tooltip={<EmptyTooltip />}
              />
            )}

            <MerchantLogoUploadForm
              onLogoChange={handleMerchantLogoChange}
              validationDisabled={merchantLogo !== undefined}
              tooltip={<EmptyTooltip />}
            />

            {merchantLogo && <MerchantLogo>{merchantLogo}</MerchantLogo>}
          </>
        )}
      </Step>

      <Step name={<FormattedMessage id="merchants.form.address" />}>
        {() => (
          <ContactAddressForm
            form={form}
            countriesDisabled={countriesDisabled}
          />
        )}
      </Step>

      <Step name={<FormattedMessage id="merchants.form.contact" />}>
        {() => (
          <>
            <ContactContactForm isContactPhoneNoRequired />

            <EmailInput
              inputID={MERCHANT_SUPPORT_EMAIL}
              label={<FormattedMessage id="merchants.form.support_email" />}
            />
          </>
        )}
      </Step>

      <Step name={<FormattedMessage id="merchants.form.privacy_tnc" />}>
        {() => (
          <>
            <MerchantTermTemplatesLoader
              countryCode={countryCode}
              termName="prv"
            >
              {(termTemplates) => (
                <TermForm
                  form={form}
                  inputID={`${MERCHANT_TERM}[prv]`}
                  termTemplates={termTemplates}
                  label={<FormattedMessage id="merchants.prv" />}
                  urlLabel={<FormattedMessage id="merchants.prv.url" />}
                  textLabel={<FormattedMessage id="merchants.prv.text" />}
                />
              )}
            </MerchantTermTemplatesLoader>

            <MerchantTermTemplatesLoader
              countryCode={countryCode}
              termName="tnc"
            >
              {(termTemplates) => (
                <TermForm
                  form={form}
                  inputID={`${MERCHANT_TERM}[tnc]`}
                  termTemplates={termTemplates}
                  label={<FormattedMessage id="merchants.tnc" />}
                  urlLabel={<FormattedMessage id="merchants.tnc.url" />}
                  textLabel={<FormattedMessage id="merchants.tnc.text" />}
                />
              )}
            </MerchantTermTemplatesLoader>

            <MerchantTermTemplatesLoader
              countryCode={countryCode}
              termName="ttc"
            >
              {(termTemplates) => (
                <TermForm
                  form={form}
                  inputID={`${MERCHANT_TERM}[ttc]`}
                  termTemplates={termTemplates}
                  label={<FormattedMessage id="merchants.ttc" />}
                  urlLabel={<FormattedMessage id="merchants.ttc.url" />}
                  textLabel={<FormattedMessage id="merchants.ttc.text" />}
                />
              )}
            </MerchantTermTemplatesLoader>
          </>
        )}
      </Step>

      {useAccessible(MerchantAccessRight.NUMBER_PROVISIONING) && (
        <Step
          name={<FormattedMessage id="merchants.form.numberProvisioning" />}
        >
          {() => (
            <MerchantFormNumberProvisioning
              form={form}
              merchant={merchant}
              countryCode={countryCode}
            />
          )}
        </Step>
      )}
    </Steps>
  );
};
